import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridReadyEvent,
  CellValueChangedEvent,
  RowClassParams,
  ValueFormatterParams,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Plus, RefreshCw } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { get, post, put } from "../api/client";
import {
  Partner,
  Tab,
  Vacature,
  CreateVacatureRequest,
  UpdateVacatureRequest,
  ExperienceLevel,
  Discipline,
  EXPERIENCE_LABELS,
  DISCIPLINE_LABELS,
} from "../types";
import VacatureModal from "../components/VacatureModal";

// ── Pill renderers ─────────────────────────────────────────────────────────────
export function StatusPill({ value }: { value: string }) {
  const styles: Record<string, string> = {
    open: "bg-green-100 text-green-700",
    gesloten: "bg-slate-100 text-slate-500",
    on_hold: "bg-yellow-100 text-yellow-700",
  };
  const labels: Record<string, string> = {
    open: "Open",
    gesloten: "Gesloten",
    on_hold: "On Hold",
  };
  return (
    <span className={`pill ${styles[value] ?? "bg-slate-100 text-slate-500"}`}>
      {labels[value] ?? value}
    </span>
  );
}

export function PriorityPill({ value }: { value: string }) {
  const styles: Record<string, string> = {
    hoog: "bg-red-100 text-red-600",
    normaal: "bg-blue-100 text-blue-700",
    laag: "bg-slate-100 text-slate-500",
  };
  const labels: Record<string, string> = {
    hoog: "Hoog",
    normaal: "Normaal",
    laag: "Laag",
  };
  return (
    <span className={`pill ${styles[value] ?? "bg-slate-100 text-slate-500"}`}>
      {labels[value] ?? value}
    </span>
  );
}

export function ExperiencePill({ value }: { value: string }) {
  const label = EXPERIENCE_LABELS[value as ExperienceLevel] ?? value;
  if (value === "nvt") {
    return (
      <span className="pill bg-slate-100 text-slate-400">
        {label}
      </span>
    );
  }
  return (
    <span className="pill bg-indigo-50 text-indigo-700">
      {label}
    </span>
  );
}

export function DisciplinePill({
  value,
  overigText,
}: {
  value: string;
  overigText?: string | null;
}) {
  const label = DISCIPLINE_LABELS[value as Discipline] ?? value;
  if (value === "nvt") {
    return <span className="pill bg-slate-100 text-slate-400">{label}</span>;
  }
  if (value === "overig") {
    return (
      <span className="pill bg-amber-50 text-amber-700" title={overigText ?? "Overig"}>
        {overigText?.trim() ? overigText : "Overig"}
      </span>
    );
  }
  return <span className="pill bg-teal-50 text-teal-700">{label}</span>;
}

export function BemiddelingPill({ value }: { value: string }) {
  const styles: Record<string, string> = {
    geen: "bg-slate-100 text-slate-500",
    in_bemiddeling: "bg-waves-blue-light text-waves-blue",
    shortlisted: "bg-blue-50 text-blue-500",
    geplaatst: "bg-green-100 text-green-700",
  };
  const labels: Record<string, string> = {
    geen: "Geen",
    in_bemiddeling: "In Bemiddeling",
    shortlisted: "Shortlisted",
    geplaatst: "Geplaatst",
  };
  return (
    <span className={`pill ${styles[value] ?? "bg-slate-100 text-slate-500"}`}>
      {labels[value] ?? value}
    </span>
  );
}

function sortVacatures(vacatures: Vacature[]): Vacature[] {
  return [...vacatures].sort((a, b) => {
    // Gesloten goes to bottom
    if (a.status === "gesloten" && b.status !== "gesloten") return 1;
    if (b.status === "gesloten" && a.status !== "gesloten") return -1;

    // Among open: hoog priority first
    if (a.status !== "gesloten" && b.status !== "gesloten") {
      const priorityOrder: Record<string, number> = { hoog: 0, normaal: 1, laag: 2 };
      const pa = priorityOrder[a.priority] ?? 1;
      const pb = priorityOrder[b.priority] ?? 1;
      if (pa !== pb) return pa - pb;
      // Same priority: sort by updated_at desc
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    }

    return 0;
  });
}

export default function Vacatures() {
  const { partnerId: partnerIdStr } = useParams<{ partnerId: string }>();
  const partnerId = Number(partnerIdStr);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTabId = searchParams.get("tab");
  const { user } = useAuth();

  const [partner, setPartner] = useState<Partner | null>(null);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = useState<number | null>(null);
  const [vacatures, setVacatures] = useState<Vacature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editVacature, setEditVacature] = useState<Vacature | null>(null);
  const gridRef = useRef<AgGridReact>(null);

  // Filters voor de twee nieuwe dimensies
  const [filterExperience, setFilterExperience] = useState<ExperienceLevel | "all">("all");
  const [filterDiscipline, setFilterDiscipline] = useState<Discipline | "all">("all");

  // Filtered tabs based on user role
  const visibleTabs = useMemo(() => {
    if (!user) return tabs;
    if (user.role === "admin" || user.role === "kam" || user.role === "partner_admin") return tabs;
    // HMP: only tabs they have permission to
    return tabs.filter(
      (t) => user.tab_permissions.includes(t.id)
    );
  }, [tabs, user]);

  const filteredVacatures = useMemo(() => {
    let list = vacatures;
    if (activeTabId) list = list.filter((v) => v.tab_id === activeTabId);
    if (filterExperience !== "all")
      list = list.filter((v) => v.experience_level === filterExperience);
    if (filterDiscipline !== "all")
      list = list.filter((v) => v.discipline === filterDiscipline);
    return sortVacatures(list);
  }, [vacatures, activeTabId, filterExperience, filterDiscipline]);

  useEffect(() => {
    if (!partnerId) return;
    setLoading(true);
    setError("");

    Promise.all([
      get<Partner[]>("/partners"),
      get<Tab[]>(`/tabs?partner_id=${partnerId}`),
      get<Vacature[]>(`/vacatures?partner_id=${partnerId}`),
    ])
      .then(([partners, tabList, vacs]) => {
        const p = partners.find((x) => x.id === partnerId) ?? null;
        setPartner(p);
        setTabs(tabList);
        setVacatures(vacs);
        if (urlTabId) {
          setActiveTabId(Number(urlTabId));
        } else {
          setActiveTabId(null); // Show all by default
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [partnerId]);

  // Sync activeTabId when URL tab param changes (sidebar navigation)
  useEffect(() => {
    if (urlTabId) {
      setActiveTabId(Number(urlTabId));
    } else {
      setActiveTabId(null);
    }
  }, [urlTabId]);

  const refresh = useCallback(() => {
    get<Vacature[]>(`/vacatures?partner_id=${partnerId}`)
      .then(setVacatures)
      .catch(() => {});
  }, [partnerId]);

  // Column definitions
  const colDefs: ColDef<Vacature>[] = useMemo(
    () => [
      {
        field: "functie",
        headerName: "Functie",
        minWidth: 160,
        maxWidth: 320,
        flex: 2,
        editable: true,
        cellStyle: { fontWeight: 500 },
      },
      {
        field: "locatie",
        headerName: "Locatie",
        minWidth: 100,
        maxWidth: 200,
        flex: 1,
        editable: true,
        valueFormatter: (p: ValueFormatterParams) => p.value ?? "—",
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 100,
        maxWidth: 140,
        width: 120,
        editable: true,
        cellRenderer: (p: { value: string }) => <StatusPill value={p.value} />,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["open", "gesloten", "on_hold"],
        },
      },
      {
        field: "priority",
        headerName: "Prioriteit",
        minWidth: 100,
        maxWidth: 140,
        width: 120,
        editable: true,
        cellRenderer: (p: { value: string }) => <PriorityPill value={p.value} />,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["hoog", "normaal", "laag"],
        },
      },
      {
        field: "contactpersoon",
        headerName: "Contactpersoon",
        minWidth: 120,
        maxWidth: 250,
        flex: 1,
        editable: true,
        valueFormatter: (p: ValueFormatterParams) => p.value ?? "—",
      },
      {
        field: "aantal_posities",
        headerName: "Aantal",
        minWidth: 70,
        maxWidth: 100,
        width: 90,
        editable: true,
        type: "numericColumn",
      },
      {
        field: "bemiddeling_status",
        headerName: "Bemiddeling",
        minWidth: 130,
        maxWidth: 180,
        width: 150,
        editable: true,
        cellRenderer: (p: { value: string }) => <BemiddelingPill value={p.value} />,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["geen", "in_bemiddeling", "shortlisted", "geplaatst"],
        },
      },
      {
        field: "experience_level",
        headerName: "Ervaring",
        minWidth: 110,
        maxWidth: 150,
        width: 130,
        editable: true,
        cellRenderer: (p: { value: string }) => <ExperiencePill value={p.value ?? "nvt"} />,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["nvt", "starter", "1_3", "3_5", "5_10", "10_plus"],
        },
      },
      {
        field: "discipline",
        headerName: "Discipline",
        minWidth: 130,
        maxWidth: 200,
        width: 160,
        editable: true,
        cellRenderer: (p: { value: string; data?: Vacature }) => (
          <DisciplinePill
            value={p.value ?? "nvt"}
            overigText={p.data?.discipline_overig_text}
          />
        ),
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: [
            "nvt",
            "combi",
            "water",
            "elektra_laag",
            "elektra_hoog",
            "gas",
            "riool",
            "overig",
          ],
        },
      },
      {
        field: "updated_at",
        headerName: "Laatste Update",
        minWidth: 120,
        maxWidth: 160,
        width: 140,
        editable: false,
        valueFormatter: (p: ValueFormatterParams) =>
          p.value
            ? new Date(p.value).toLocaleDateString("nl-NL", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "—",
        sort: "desc",
      },
    ],
    [user]
  );

  async function handleCellValueChanged(event: CellValueChangedEvent<Vacature>) {
    if (!event.data) return;
    const id = event.data.id;
    const field = event.colDef.field as keyof Vacature;
    if (!field) return;

    try {
      const update: UpdateVacatureRequest = {
        [field]: event.newValue,
      };
      await put<Vacature>(`/vacatures/${id}`, update);
      // Refresh to ensure grid has updated_at
      refresh();
    } catch (err) {
      console.error("Update mislukt:", err);
      // Revert
      event.node.setDataValue(field, event.oldValue);
    }
  }

  async function handleCreate(data: CreateVacatureRequest | UpdateVacatureRequest) {
    await post<Vacature>("/vacatures", data);
    refresh();
  }

  async function handleEdit(data: CreateVacatureRequest | UpdateVacatureRequest) {
    if (!editVacature) return;
    await put<Vacature>(`/vacatures/${editVacature.id}`, data);
    refresh();
    setEditVacature(null);
  }

  function getRowClass(params: RowClassParams<Vacature>): string {
    if (params.data?.status === "gesloten") return "row-gesloten";
    return "";
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-sm">
        <strong>Fout bij laden:</strong> {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Page header — stacked on mobile, inline on desktop */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800">{partner?.name ?? "Partner"}</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {filteredVacatures.length} vacature{filteredVacatures.length !== 1 ? "s" : ""}
            {activeTabId
              ? ` in ${visibleTabs.find((t) => t.id === activeTabId)?.name ?? "geselecteerde tab"}`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="btn-secondary flex items-center gap-2 min-h-[44px] md:min-h-0"
            title="Vernieuwen"
          >
            <RefreshCw size={14} />
            <span className="hidden sm:inline">Vernieuwen</span>
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center gap-2 flex-1 md:flex-none justify-center min-h-[44px] md:min-h-0 text-sm md:text-base"
          >
            <Plus size={16} />
            Nieuwe Vacature
          </button>
        </div>
      </div>

      {/* Filters voor ervaring + discipline */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
        <div className="flex items-center gap-2 flex-1">
          <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
            Ervaring
          </label>
          <select
            value={filterExperience}
            onChange={(e) =>
              setFilterExperience(e.target.value as ExperienceLevel | "all")
            }
            className="select-field text-sm"
          >
            <option value="all">Alle</option>
            <option value="nvt">{EXPERIENCE_LABELS.nvt}</option>
            <option value="starter">{EXPERIENCE_LABELS.starter}</option>
            <option value="1_3">{EXPERIENCE_LABELS["1_3"]}</option>
            <option value="3_5">{EXPERIENCE_LABELS["3_5"]}</option>
            <option value="5_10">{EXPERIENCE_LABELS["5_10"]}</option>
            <option value="10_plus">{EXPERIENCE_LABELS["10_plus"]}</option>
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1">
          <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
            Discipline
          </label>
          <select
            value={filterDiscipline}
            onChange={(e) =>
              setFilterDiscipline(e.target.value as Discipline | "all")
            }
            className="select-field text-sm"
          >
            <option value="all">Alle</option>
            <option value="nvt">{DISCIPLINE_LABELS.nvt}</option>
            <option value="combi">{DISCIPLINE_LABELS.combi}</option>
            <option value="water">{DISCIPLINE_LABELS.water}</option>
            <option value="elektra_laag">{DISCIPLINE_LABELS.elektra_laag}</option>
            <option value="elektra_hoog">{DISCIPLINE_LABELS.elektra_hoog}</option>
            <option value="gas">{DISCIPLINE_LABELS.gas}</option>
            <option value="riool">{DISCIPLINE_LABELS.riool}</option>
            <option value="overig">{DISCIPLINE_LABELS.overig}</option>
          </select>
        </div>
        {(filterExperience !== "all" || filterDiscipline !== "all") && (
          <button
            onClick={() => {
              setFilterExperience("all");
              setFilterDiscipline("all");
            }}
            className="text-xs text-slate-500 hover:text-slate-700 underline self-start sm:self-center"
          >
            Filters wissen
          </button>
        )}
      </div>

      {/* Tab bar */}
      {visibleTabs.length > 0 && (
        <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1 overflow-x-auto flex-shrink-0 -webkit-overflow-scrolling-touch">
          <button
            onClick={() => {
              setActiveTabId(null);
              setSearchParams({});
            }}
            className={`px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-150 min-h-[36px] ${
              activeTabId === null
                ? "bg-waves-blue text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            Alle vestigingen
          </button>
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTabId(tab.id);
                setSearchParams({ tab: String(tab.id) });
              }}
              className={`px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-150 min-h-[36px] ${
                activeTabId === tab.id
                  ? "bg-waves-blue text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {tab.name}
              <span
                className={`ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                  activeTabId === tab.id
                    ? "bg-white/20 text-white"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {vacatures.filter((v) => v.tab_id === tab.id).length}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* AG Grid — desktop only */}
      <div className="card flex-1 overflow-hidden hidden md:block">
        <div className="ag-theme-alpine w-full h-full" style={{ minHeight: 400 }}>
          <AgGridReact<Vacature>
            ref={gridRef}
            rowData={filteredVacatures}
            columnDefs={colDefs}
            animateRows={true}
            rowSelection="single"
            getRowClass={getRowClass}
            onCellValueChanged={handleCellValueChanged}
            stopEditingWhenCellsLoseFocus={true}
            singleClickEdit={false}
            suppressMovableColumns={false}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: false,
            }}
            suppressHorizontalScroll={false}
            alwaysShowHorizontalScroll={false}
            loadingOverlayComponent={() => (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <div className="w-4 h-4 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
                Laden…
              </div>
            )}
            noRowsOverlayComponent={() => (
              <div className="text-slate-400 text-sm">Geen vacatures gevonden</div>
            )}
          />
        </div>
      </div>

      {/* Card list — mobile only */}
      <div className="block md:hidden flex-1">
        {filteredVacatures.length === 0 ? (
          <div className="card p-8 text-center text-slate-400 text-sm">
            Geen vacatures gevonden
          </div>
        ) : (
          <div className="space-y-3">
            {filteredVacatures.map((v) => (
              <div
                key={v.id}
                className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 text-sm truncate">{v.functie}</h3>
                    {v.locatie && (
                      <p className="text-xs text-slate-500 mt-0.5">{v.locatie}</p>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <StatusPill value={v.status} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <PriorityPill value={v.priority} />
                  <BemiddelingPill value={v.bemiddeling_status} />
                  <ExperiencePill value={v.experience_level ?? "nvt"} />
                  <DisciplinePill
                    value={v.discipline ?? "nvt"}
                    overigText={v.discipline_overig_text}
                  />
                  {v.contactpersoon && (
                    <span className="text-xs text-slate-500">👤 {v.contactpersoon}</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <span className="text-xs text-slate-400">
                    {new Date(v.updated_at).toLocaleDateString("nl-NL", {
                      day: "2-digit",
                      month: "short",
                    })}
                  </span>
                  <button
                    onClick={() => setEditVacature(v)}
                    className="text-xs text-waves-blue font-medium hover:underline min-h-[44px] flex items-center"
                  >
                    Bewerken
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showModal && (
        <VacatureModal
          mode="create"
          partnerId={partnerId}
          tabs={visibleTabs}
          activeTabId={activeTabId ?? undefined}
          onSubmit={handleCreate}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Edit modal */}
      {editVacature && (
        <VacatureModal
          mode="edit"
          partnerId={partnerId}
          tabs={visibleTabs}
          activeTabId={activeTabId ?? undefined}
          vacature={editVacature}
          onSubmit={handleEdit}
          onClose={() => setEditVacature(null)}
        />
      )}
    </div>
  );
}
